"use client";
import { useState } from "react";
import PropTypes from "prop-types";
import classes from "./input.module.css";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa6";
import { Label } from "../Label";
import { floatRegEx, numberRegEx, pointRegEx } from "constant/regex";

/**
 * Primary UI component for user interaction
 */
export const Input = ({
  type,
  label,
  label2, // sub label
  value, // input value
  setter, //setValue
  noBorder,
  placeholder,
  disabled,
  parentCustomStyle, //Main Div Inline Style
  customStyle, //Input Container inline Style
  inputStyle, //Input inline Style
  labelStyle, //Label inline Style
  error, // Show Error Boolean
  errorText, // Error Text
  leftIcon, // Icon For Input
  rightIcon,
  regexType,
  labelOnTop = false,
  onEnter,
  variant = "square",
  autoComplete, // "off" is ignored by browsers — normalized below
  name,
  ...props
}) => {
  const [passToggle, setPassToggle] = useState(false);
  // Chrome/Edge ignore autoComplete="off" on address/email-like fields and still
  // show their saved-address dropdown. An unrecognized token disables it for good.
  const [noAutoFillToken] = useState(
    () => `nofill-${Math.random().toString(36).slice(2)}`,
  );
  let inputContainerStyleObject = Object.assign(
    {},
    error && { border: `1px solid red ` },
    leftIcon && { paddingLeft: "50px" },
  );
  return (
    <>
      <div
        className={`${[
          classes.Container,
          labelOnTop ? classes.labelOnTop : "",
        ].join(" ")}`}
        style={{ ...parentCustomStyle }}
      >
        {label && (
          <Label
            htmlFor={`input${label}`}
            disabled={disabled}
            labelOnTop={labelOnTop}
            style={{ ...labelStyle }}
          >
            {label} {label2 && label2}
          </Label>
        )}
        <div
          className={`${[classes.inputPassContainer].join(" ")}`}
          style={{ ...customStyle }}
        >
          {leftIcon && <div className={classes.leftIconBox}>{leftIcon}</div>}
          <input
            value={value}
            onKeyDown={(e) => {
              if (onEnter) {
                if (value === "") return;
                if (e.key == "Enter") {
                  onEnter();
                }
              }
            }}
            onChange={(e) => {
              if (regexType == "point") {
                if (pointRegEx.test(e?.target?.value)) {
                  setter(e?.target?.value);
                }
              } else if (regexType === "float") {
                const value = e?.target?.value;
                if (value === "" || floatRegEx.test(value)) {
                  setter(value);
                }
              } else if (regexType == "number" || type == "number") {
                setter(
                  Number(e?.target?.value?.replace(numberRegEx, "")) || "",
                );
              } else {
                setter(e.target.value);
              }
            }}
            disabled={disabled}
            placeholder={placeholder}
            type={passToggle == true ? "text" : type}
            id={`input${label}`}
            data-variant={variant}
            className={` ${[
              classes.inputBox,
              noBorder && classes.noBorder,
            ].join(" ")}`}
            style={{ ...inputContainerStyleObject, ...inputStyle }}
            onBlur={(e) => {
              setter(e.target.value?.trim());
            }}
            name={name || noAutoFillToken}
            autoComplete={
              type === "password"
                ? "new-password"
                : !autoComplete || autoComplete === "off"
                  ? noAutoFillToken
                  : autoComplete
            }
            data-lpignore="true"
            data-form-type="other"
            data-1p-ignore="true"
            {...props}
          />
          {rightIcon && <div className={classes.rightIcon}>{rightIcon}</div>}

          {type == "password" && passToggle == false && (
            <FaRegEyeSlash
              className={classes.passwordIcon}
              onClick={(e) => setPassToggle(!passToggle)}
            />
          )}
          {type == "password" && passToggle && (
            <FaRegEye
              className={classes.passwordIcon}
              onClick={(e) => setPassToggle(!passToggle)}
            />
          )}
          {error && (
            <p
              className={`${[
                classes.errorText,
                type == "password" || rightIcon
                  ? classes.offsetLeftError
                  : undefined,
              ].join(" ")}`}
            >
              {errorText}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

Input.propTypes = {
  type: PropTypes.oneOf.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string.isRequired,
  setter: PropTypes.string,
  noBorder: PropTypes.bool,
  disabled: PropTypes.bool,
  customStyle: PropTypes.string,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  label2: PropTypes.string,
};

Input.defaultProps = {
  type: "text",
  placeholder: "enter text",
  value: "",
  noBorder: false,
  disabled: false,
  error: false,
  errorText: "An error has occurred, check your details!",
};
